import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useWallet } from '../context/WalletContext';
import { useAuth } from '../context/AuthContext';
import '../styles/profile.css';

const Profile = () => {
  const navigate = useNavigate();
  const { account, formatAddress } = useWallet();
  const { currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('datasets');
  const [editMode, setEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({
    name: '',
    bio: '',
    website: '',
    twitter: ''
  });

  // If user is not logged in, they should be redirected by the ProtectedRoute
  // This is just an extra safety check
  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
    }
  }, [currentUser, navigate]);

  // Mock data for initial development
  useEffect(() => {
    // This would be replaced with actual API calls in production
    const mockProfile = {
      address: account || '0x1234...5678',
      name: currentUser ? currentUser.name : 'AI Researcher',
      bio: 'Data scientist specializing in computer vision and NLP. Working on deep learning models for image classification and text generation.',
      website: 'https://airesearcher.com',
      twitter: '@ai_researcher',
      profileImage: currentUser ? currentUser.profileImage : 'https://randomuser.me/api/portraits/men/32.jpg',
      reputationScore: 4.7,
      memberSince: '2023-05-15',
      totalRevenue: 1.08,
      totalDatasets: 2,
      totalPurchases: 1,
      datasets: [
        {
          id: 1,
          name: 'MNIST Dataset',
          description: 'Handwritten digits dataset for image classification',
          category: 'computer-vision',
          price: 0.05,
          purchases: 12,
          revenue: 0.6,
          rating: 4.5,
          lastUpdated: '2023-10-10',
          status: 'active'
        },
        {
          id: 2,
          name: 'Fashion MNIST',
          description: 'Clothing items dataset for image classification',
          category: 'computer-vision',
          price: 0.06,
          purchases: 8,
          revenue: 0.48,
          rating: 4.2,
          lastUpdated: '2023-09-22',
          status: 'active'
        }
      ],
      purchases: [
        {
          id: 101,
          name: 'IMDb Reviews',
          description: 'Movie reviews for sentiment analysis',
          category: 'nlp',
          seller: 'NLP Expert',
          sellerAddress: '0xabcd...efgh',
          purchaseDate: '2023-10-15',
          price: 0.03,
          downloadUrl: '#'
        }
      ],
      transactions: [
        {
          id: 'tx1',
          type: 'sale',
          item: 'MNIST Dataset',
          buyer: '0xabcd...1234',
          amount: 0.05,
          date: '2023-11-01',
          txHash: '0x1234...abcd'
        },
        {
          id: 'tx2',
          type: 'purchase',
          item: 'IMDb Reviews',
          seller: '0xabcd...efgh',
          amount: 0.03,
          date: '2023-10-15',
          txHash: '0x5678...efgh'
        }
      ]
    };

    setTimeout(() => {
      setProfile(mockProfile);
      setProfileForm({
        name: mockProfile.name,
        bio: mockProfile.bio,
        website: mockProfile.website,
        twitter: mockProfile.twitter
      });
      setLoading(false);
    }, 1000); // Simulate network delay
  }, []);

  const handleProfileFormChange = (e) => {
    const { name, value } = e.target;
    setProfileForm({
      ...profileForm,
      [name]: value
    });
  };

  const saveProfileChanges = () => {
    // In a real implementation, this would send the updated profile to the backend
    setProfile({
      ...profile,
      ...profileForm
    });
    setEditMode(false);
  };

  // Function to render star ratings
  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalfStar = rating % 1 >= 0.5;

    for (let i = 1; i <= 5; i++) {
      if (i <= fullStars) {
        stars.push(<i key={i} className="fas fa-star"></i>);
      } else if (i === fullStars + 1 && hasHalfStar) {
        stars.push(<i key={i} className="fas fa-star-half-alt"></i>);
      } else {
        stars.push(<i key={i} className="far fa-star"></i>);
      }
    }

    return <div className="star-rating">{stars} <span className="rating-value">({rating})</span></div>;
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  // We don't need to check for account here anymore since we're using the auth system
  // The wallet connection is now optional

  if (!profile) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      <div className="profile-header-bg"></div>

      <div className="container">
        <div className="profile-content">
          <div className="profile-sidebar">
            <div className="profile-card">
              <div className="profile-image">
                <img src={profile.profileImage} alt={profile.name} />
              </div>

              {editMode ? (
                <div className="profile-edit-form">
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={profileForm.name}
                      onChange={handleProfileFormChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="bio">Bio</label>
                    <textarea
                      id="bio"
                      name="bio"
                      value={profileForm.bio}
                      onChange={handleProfileFormChange}
                      className="form-control"
                      rows="4"
                    ></textarea>
                  </div>

                  <div className="form-group">
                    <label htmlFor="website">Website</label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={profileForm.website}
                      onChange={handleProfileFormChange}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="twitter">Twitter</label>
                    <input
                      type="text"
                      id="twitter"
                      name="twitter"
                      value={profileForm.twitter}
                      onChange={handleProfileFormChange}
                      className="form-control"
                    />
                  </div>

                  <div className="profile-actions">
                    <button className="btn btn-secondary" onClick={() => setEditMode(false)}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={saveProfileChanges}>
                      Save Changes
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="profile-info">
                    <h2 className="profile-name">{profile.name}</h2>
                    <div className="profile-address">
                      <i className="fas fa-wallet"></i>
                      <span>{profile.address}</span>
                      <button className="copy-btn" title="Copy address">
                        <i className="far fa-copy"></i>
                      </button>
                    </div>

                    <div className="profile-reputation">
                      <div className="reputation-label">Reputation Score</div>
                      {renderStars(profile.reputationScore)}
                    </div>

                    <div className="profile-bio">
                      <p>{profile.bio}</p>
                    </div>

                    <div className="profile-links">
                      {profile.website && (
                        <a href={profile.website} target="_blank" rel="noopener noreferrer" className="profile-link">
                          <i className="fas fa-globe"></i> Website
                        </a>
                      )}
                      {profile.twitter && (
                        <a href={`https://twitter.com/${profile.twitter.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="profile-link">
                          <i className="fab fa-twitter"></i> Twitter
                        </a>
                      )}
                    </div>
                  </div>

                  <div className="profile-stats">
                    <div className="stat-item">
                      <div className="stat-value">{profile.totalDatasets}</div>
                      <div className="stat-label">Datasets</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{profile.totalRevenue} ETH</div>
                      <div className="stat-label">Revenue</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{profile.totalPurchases}</div>
                      <div className="stat-label">Purchases</div>
                    </div>
                  </div>

                  <div className="profile-actions">
                    <button className="btn btn-primary" onClick={() => setEditMode(true)}>
                      <i className="fas fa-edit"></i> Edit Profile
                    </button>
                  </div>
                </>
              )}

              <div className="profile-footer">
                <div className="member-since">
                  <i className="fas fa-calendar-alt"></i> Member since {new Date(profile.memberSince).toLocaleDateString()}
                </div>
              </div>
            </div>
          </div>

          <div className="profile-main">
            <div className="profile-tabs">
              <button
                className={`tab-btn ${activeTab === 'datasets' ? 'active' : ''}`}
                onClick={() => setActiveTab('datasets')}
              >
                <i className="fas fa-database"></i> My Datasets
              </button>
              <button
                className={`tab-btn ${activeTab === 'purchases' ? 'active' : ''}`}
                onClick={() => setActiveTab('purchases')}
              >
                <i className="fas fa-download"></i> My Purchases
              </button>
              <button
                className={`tab-btn ${activeTab === 'transactions' ? 'active' : ''}`}
                onClick={() => setActiveTab('transactions')}
              >
                <i className="fas fa-exchange-alt"></i> Transactions
              </button>
            </div>

            <div className="tab-content">
              {activeTab === 'datasets' && (
                <div className="datasets-tab">
                  <div className="tab-header">
                    <h2>My Datasets</h2>
                    <Link to="/upload" className="btn btn-primary">
                      <i className="fas fa-plus"></i> Upload New Dataset
                    </Link>
                  </div>

                  {profile.datasets.length > 0 ? (
                    <div className="dataset-list">
                      {profile.datasets.map(dataset => (
                        <div key={dataset.id} className="dataset-card">
                          <div className="dataset-header">
                            <h3 className="dataset-title">
                              <Link to={`/dataset/${dataset.id}`}>{dataset.name}</Link>
                            </h3>
                            <div className="dataset-category">
                              <span className="category-badge">{dataset.category}</span>
                              <span className={`status-badge ${dataset.status}`}>{dataset.status}</span>
                            </div>
                          </div>

                          <div className="dataset-body">
                            <p className="dataset-description">{dataset.description}</p>

                            <div className="dataset-stats">
                              <div className="stat">
                                <i className="fas fa-download"></i>
                                <span>{dataset.purchases} downloads</span>
                              </div>
                              <div className="stat">
                                <i className="fas fa-star"></i>
                                <span>{dataset.rating} rating</span>
                              </div>
                              <div className="stat">
                                <i className="fas fa-calendar-alt"></i>
                                <span>Updated {dataset.lastUpdated}</span>
                              </div>
                            </div>
                          </div>

                          <div className="dataset-footer">
                            <div className="dataset-price">
                              <span className="price-value">{dataset.price} ETH</span>
                              <span className="revenue">Revenue: {dataset.revenue} ETH</span>
                            </div>

                            <div className="dataset-actions">
                              <Link to={`/dataset/${dataset.id}/edit`} className="btn btn-secondary">
                                <i className="fas fa-edit"></i> Edit
                              </Link>
                              <Link to={`/dataset/${dataset.id}/analytics`} className="btn btn-secondary">
                                <i className="fas fa-chart-line"></i> Analytics
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <i className="fas fa-database empty-icon"></i>
                      <h3>No datasets yet</h3>
                      <p>You haven't uploaded any datasets yet. Start sharing your data with the community!</p>
                      <Link to="/upload" className="btn btn-primary">
                        <i className="fas fa-plus"></i> Upload Your First Dataset
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'purchases' && (
                <div className="purchases-tab">
                  <div className="tab-header">
                    <h2>My Purchases</h2>
                    <Link to="/marketplace" className="btn btn-primary">
                      <i className="fas fa-search"></i> Explore More Datasets
                    </Link>
                  </div>

                  {profile.purchases.length > 0 ? (
                    <div className="purchase-list">
                      {profile.purchases.map(purchase => (
                        <div key={purchase.id} className="purchase-card">
                          <div className="purchase-header">
                            <h3 className="purchase-title">
                              <Link to={`/dataset/${purchase.id}`}>{purchase.name}</Link>
                            </h3>
                            <span className="category-badge">{purchase.category}</span>
                          </div>

                          <div className="purchase-body">
                            <p className="purchase-description">{purchase.description}</p>

                            <div className="purchase-meta">
                              <div className="seller-info">
                                <span className="meta-label">Seller:</span>
                                <span className="meta-value">{purchase.seller}</span>
                              </div>
                              <div className="purchase-date">
                                <span className="meta-label">Purchased:</span>
                                <span className="meta-value">{purchase.purchaseDate}</span>
                              </div>
                              <div className="purchase-price">
                                <span className="meta-label">Price:</span>
                                <span className="meta-value">{purchase.price} ETH</span>
                              </div>
                            </div>
                          </div>

                          <div className="purchase-footer">
                            <a href={purchase.downloadUrl} className="btn btn-primary download-btn">
                              <i className="fas fa-download"></i> Download
                            </a>
                            <Link to={`/dataset/${purchase.id}`} className="btn btn-secondary">
                              <i className="fas fa-info-circle"></i> Details
                            </Link>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="empty-state">
                      <i className="fas fa-shopping-cart empty-icon"></i>
                      <h3>No purchases yet</h3>
                      <p>You haven't purchased any datasets yet. Explore the marketplace to find valuable data!</p>
                      <Link to="/marketplace" className="btn btn-primary">
                        <i className="fas fa-search"></i> Browse Marketplace
                      </Link>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'transactions' && (
                <div className="transactions-tab">
                  <div className="tab-header">
                    <h2>Transaction History</h2>
                  </div>

                  {profile.transactions.length > 0 ? (
                    <div className="transactions-table-container">
                      <table className="transactions-table">
                        <thead>
                          <tr>
                            <th>Type</th>
                            <th>Item</th>
                            <th>From/To</th>
                            <th>Amount</th>
                            <th>Date</th>
                            <th>Transaction</th>
                          </tr>
                        </thead>
                        <tbody>
                          {profile.transactions.map(tx => (
                            <tr key={tx.id}>
                              <td>
                                <span className={`tx-type ${tx.type}`}>
                                  {tx.type === 'sale' ? (
                                    <><i className="fas fa-arrow-up"></i> Sale</>
                                  ) : (
                                    <><i className="fas fa-arrow-down"></i> Purchase</>
                                  )}
                                </span>
                              </td>
                              <td>{tx.item}</td>
                              <td>
                                {tx.type === 'sale' ? tx.buyer : tx.seller}
                              </td>
                              <td className="amount">{tx.amount} ETH</td>
                              <td>{tx.date}</td>
                              <td>
                                <a href={`https://etherscan.io/tx/${tx.txHash}`} target="_blank" rel="noopener noreferrer" className="tx-link">
                                  <i className="fas fa-external-link-alt"></i> View
                                </a>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="empty-state">
                      <i className="fas fa-exchange-alt empty-icon"></i>
                      <h3>No transactions yet</h3>
                      <p>Your transaction history will appear here once you've made purchases or sales.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
